import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, BarChart2, AlertTriangle, ClipboardList, CalendarDays,
  FileWarning, Building2, UserCircle, Activity, Settings,
  ListChecks, Search, LogOut, Menu, X, Lock, Wallet, KeyRound,
  ClipboardCheck, Users, ArrowLeftRight, Library, PieChart, Calculator,
  Receipt, TrendingUp, Sparkles, Camera, GitBranch, Truck, ChevronDown, CalendarRange,
} from 'lucide-react';
import useAuthStore from '../store/authStore';
import { isSalesMember } from '../utils/access';

// Design Calculators is restricted to these roles - same set as the
// backend's require_design_access in design_routes.py. This only hides
// the nav entry; the real enforcement is server-side.
// 2026-08-15: narrowed from (Engineer, HOD, CEO, System_Admin) to just
// (HOD, CEO) per Komal's explicit request.
const DESIGN_ROLES = ['HOD', 'CEO'];

// Ask Komal (AI assistant) is rolled out to these roles only for now - same
// set as the backend's require_ai_access in ai_routes.py. This only hides
// the nav entry; the real enforcement is server-side. Widen both when it's
// ready for everyone.
const AI_ROLES = ['HOD', 'CEO', 'HR'];

// Payroll link (inside the merged "HR" section below) is restricted to
// these roles - matches the backend's require_role("HR", "CEO",
// "System_Admin") in payroll_routes.py. This only hides the nav entry;
// the real enforcement is server-side. Dashboard/Employee Master/Payroll
// Runs are reached via the PayrollTabs bar once inside the module, same
// as Design Calculators' own sub-navigation, so only one sidebar entry
// is needed for the whole module.
const PAYROLL_ROLES = ['HR', 'CEO', 'System_Admin'];

// CRM module access - matches the backend's require_crm_access in
// crm_routes.py (Sales Department employees, plus CEO always). Checked by
// department NAME, not role, same reasoning as that backend function's
// docstring: role encodes position level (HOD/Engineer/...), not which
// department someone's in - a simple rolesVisible array (used by most
// links below) can't express that, so this is its own function, used as
// the CRM section's sectionVisible. UI convenience only; real enforcement
// is server-side.
const canSeeCrm = (u) => u?.role === 'CEO' || ['Sales', 'Sales Department'].includes(u?.department); // 'Sales Department' = pre-2026-09-20 name, still in a stale session's cached user

// Vendor Directory access - matches the backend's require_vendor_access in
// vendor_routes.py: CEO always, plus the HOD of any of these departments
// (2026-09-10 decision). Same department-name reasoning as canSeeCrm
// above - used as this one link's own `visible`, since every other link
// in Operations has no such restriction.
const VENDOR_DIRECTORY_DEPARTMENTS = ['Design', 'Purchase', 'Projects', 'Production', 'Operations', 'Project', 'Design Department', 'Procurement Department', 'Project Department'];
const canSeeVendors = (u) => u?.role === 'CEO' || (u?.role === 'HOD' && VENDOR_DIRECTORY_DEPARTMENTS.includes(u?.department));

// Each section renders as a collapsible group, closed by default except
// whichever one holds the page you're currently on (see the auto-open
// effect below) - 2026-09-11 redesign to cut down how much a new user
// scrolls past. Inputs, Production and Quality merged into one Operations
// group (all daily site/production work); Design and the old standalone
// Procurement section folded away (Design promoted to a standalone link
// next to Dashboard, Vendors folded into Operations) since a section
// header for a single link was pure overhead.
const SECTIONS = [
  {
    label: 'Operations',
    icon: Activity,
    links: [
      { to: '/punch-points', label: 'Task and Issues Register', icon: ClipboardList },
      { to: '/schedule/my-tasks', label: 'Schedule Updates', icon: CalendarRange },
      { to: '/schedule/reviews', label: 'Weekly Review', icon: ClipboardCheck },
      { to: '/daily-entry', label: 'Daily Entry', icon: CalendarDays },
      { to: '/production', label: 'Production Tracking', icon: Activity },
      { to: '/ncr', label: 'NCR', icon: FileWarning },
      { to: '/quality/checklists', label: 'Checklists', icon: ListChecks },
      { to: '/quality/inspections', label: 'Inspections', icon: Search },
      { to: '/vendors', label: 'Vendors', icon: Truck, visible: canSeeVendors },
    ],
  },
  {
    label: 'Reports',
    icon: BarChart2,
    links: [
      { to: '/performance', label: 'Performance', icon: BarChart2, visible: (u) => !isSalesMember(u) },
      { to: '/delays', label: 'Delays', icon: AlertTriangle },
    ],
  },
  {
    label: 'Finance',
    icon: Wallet,
    links: [
      { to: '/payments', label: 'Payments', icon: Wallet },
      { to: '/expenses', label: 'Expenses', icon: Receipt },
      { to: '/cashflow', label: 'Cash Flow', icon: TrendingUp },
    ],
  },
  {
    // Merged 2026-09-13 request: Payroll (previously its own standalone
    // sidebar link) and Appraisals (previously its own section) now live
    // under one "HR" section. No sectionVisible on the section itself -
    // My KRA/Cross Inputs stay visible to everyone the way they always
    // were; only the Payroll link and the role-gated KRA links (Team KRA/
    // KRA Library/Quarterly Dashboard) carry their own rolesVisible, same
    // as before the merge. groupLabel on the first link of each subgroup
    // renders a small divider so Payroll and Appraisals still read as two
    // distinct things within the one section.
    label: 'HR',
    icon: ClipboardCheck,
    links: [
      { to: '/payroll/dashboard', label: 'Payroll', icon: Wallet, rolesVisible: PAYROLL_ROLES, groupLabel: 'Payroll' },
      { to: '/kra', label: 'My KRA', icon: ClipboardCheck, groupLabel: 'Appraisals' },
      { to: '/kra/team', label: 'Team KRA', icon: Users, rolesVisible: ['HOD', 'HR', 'CEO'] },
      { to: '/kra/cross-inputs', label: 'Cross Inputs', icon: ArrowLeftRight },
      { to: '/kra/library', label: 'KRA Library', icon: Library, rolesVisible: ['HR', 'CEO'] },
      { to: '/kra/quarterly', label: 'Quarterly Dashboard', icon: PieChart, rolesVisible: ['CEO', 'HR'] },
    ],
  },
  {
    label: 'CRM',
    icon: Users,
    sectionVisible: canSeeCrm,
    links: [
      { to: '/crm/dashboard', label: 'Dashboard', icon: PieChart },
      { to: '/crm/pipeline', label: 'Pipeline', icon: GitBranch },
      { to: '/crm/accounts', label: 'Customers', icon: Building2 },
      { to: '/crm/scan', label: 'Scan & Review', icon: Camera },
    ],
  },
  {
    label: 'User Management',
    icon: Settings,
    links: [
      { to: '/projects', label: 'Projects', icon: Building2 },
      { to: '/people', label: 'People', icon: UserCircle },
      { to: '/admin/categories', label: 'Categories', icon: Settings },
      { to: '/admin/permissions', label: 'Permissions', icon: Lock },
    ],
  },
];

// A link "belongs" to its section for auto-open purposes if the current
// path matches it exactly or is a sub-route of it (e.g. viewing one CRM
// customer's detail page, /crm/accounts/5, should still auto-open CRM even
// though its own nav entry only points at /crm/accounts).
const pathBelongsTo = (to, pathname) => pathname === to || pathname.startsWith(`${to}/`);

const Sidebar = () => {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openSections, setOpenSections] = useState(() => new Set());

  // Auto-open whichever section holds the page currently being viewed -
  // additive only, so a section the user opened by hand elsewhere doesn't
  // get closed again by navigating somewhere else.
  useEffect(() => {
    const match = SECTIONS.find((s) => s.links.some((l) => pathBelongsTo(l.to, location.pathname)));
    if (!match) return;
    setOpenSections((prev) => (prev.has(match.label) ? prev : new Set(prev).add(match.label)));
  }, [location.pathname]);

  const toggleSection = (label) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label); else next.add(label);
      return next;
    });
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (to) => location.pathname === to;

  const linkClasses = (to) =>
    `flex items-center gap-2.5 px-3 py-2 rounded text-sm transition-colors ${
      isActive(to) ? 'bg-komal-blue text-white font-semibold' : 'text-gray-300 hover:bg-white/10 hover:text-white'
    }`;

  const sidebarContent = (
    <div className="flex flex-col h-full">
      <div className="px-4 py-5 border-b border-white/10 flex items-center justify-between">
        <img src="/assets/logo-white.png" alt="Komal" className="h-9 w-auto" />
        <button className="md:hidden text-gray-300 hover:text-white" onClick={() => setMobileOpen(false)}>
          <X size={20} />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        <Link to="/dashboard" className={linkClasses('/dashboard')} onClick={() => setMobileOpen(false)}>
          <LayoutDashboard size={16} />
          Dashboard
        </Link>
        {AI_ROLES.includes(user?.role) && (
          <Link to="/ai-assistant" className={linkClasses('/ai-assistant')} onClick={() => setMobileOpen(false)}>
            <Sparkles size={16} />
            Ask Komal
          </Link>
        )}
        {DESIGN_ROLES.includes(user?.role) && (
          <Link to="/design" className={linkClasses('/design')} onClick={() => setMobileOpen(false)}>
            <Calculator size={16} />
            Design Calculators
          </Link>
        )}
        <div className="pt-3 space-y-1">
          {SECTIONS.map((section) => {
            if (section.sectionVisible && !section.sectionVisible(user)) return null;
            const visibleLinks = section.links.filter(
              (link) =>
                (!link.rolesVisible || link.rolesVisible.includes(user?.role)) &&
                (!link.visible || link.visible(user))
            );
            if (visibleLinks.length === 0) return null;
            const open = openSections.has(section.label);
            // A link's own groupLabel renders a small divider above it
            // whenever it differs from the previous visible link's group -
            // lets a merged section (e.g. "HR" = Payroll + Appraisals)
            // still read as two distinct things, without a nested toggle.
            let lastGroup = null;
            return (
              <div key={section.label}>
                <button
                  onClick={() => toggleSection(section.label)}
                  className="w-full flex items-center gap-2.5 px-3 py-2 rounded text-xs font-semibold uppercase tracking-wide text-gray-400 hover:bg-white/10 hover:text-gray-200"
                >
                  <section.icon size={15} className="shrink-0" />
                  <span className="flex-1 text-left">{section.label}</span>
                  <ChevronDown size={14} className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
                </button>
                {open && (
                  <div className="space-y-1 mt-0.5 mb-1">
                    {visibleLinks.map((link) => {
                      const showGroupHeader = link.groupLabel && link.groupLabel !== lastGroup;
                      lastGroup = link.groupLabel || lastGroup;
                      return (
                        <React.Fragment key={link.to}>
                          {showGroupHeader && (
                            <div className="px-3 pt-1.5 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                              {link.groupLabel}
                            </div>
                          )}
                          <Link to={link.to} className={linkClasses(link.to)} onClick={() => setMobileOpen(false)}>
                            <link.icon size={16} />
                            {link.label}
                          </Link>
                        </React.Fragment>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </nav>

      <div className="px-4 py-4 border-t border-white/10">
        {user && (
          <div className="text-xs text-gray-300 mb-2 truncate">
            {user.name} <span className="text-gray-500">({user.role})</span>
          </div>
        )}
        <Link
          to="/change-password"
          onClick={() => setMobileOpen(false)}
          className="w-full flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded text-gray-200 text-xs font-semibold mb-2"
        >
          <KeyRound size={14} />
          Change Password
        </Link>
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 px-3 py-1.5 rounded text-white text-xs font-semibold"
        >
          <LogOut size={14} />
          Logout
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile top bar */}
      <div className="md:hidden glass-sidebar fixed top-0 left-0 right-0 z-30 text-white flex items-center justify-between px-4 py-3">
        <button onClick={() => setMobileOpen(true)} className="text-gray-200">
          <Menu size={22} />
        </button>
        <img src="/assets/logo-white.png" alt="Komal" className="h-7 w-auto" />
        <span className="w-[22px]" />
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className="sidebar-aside glass-sidebar fixed top-0 bottom-0 left-0 z-50 w-64 transition-transform"
        style={mobileOpen ? { transform: 'translateX(0)' } : undefined}
      >
        {sidebarContent}
      </aside>
    </>
  );
};

export default Sidebar;
